/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * The working day where the reader is.
 *
 * `new Date().getHours()` was close but not the same thing: it reads the
 * machine's clock without ever naming a zone, so nothing downstream could tell
 * which day it meant, and a week beginning on "the browser's Monday" starts on
 * the wrong day for anyone near midnight elsewhere.
 *
 * Here the zone is named and asked for explicitly, defaulting to the one the
 * browser reports — which is the best answer to "where did they sign in from"
 * that a web application has. Somebody in London gets London's morning and
 * somebody in New York gets New York's, without either being told about the
 * other.
 *
 * Worth knowing: this is the machine's setting, not its location. A laptop
 * carried from San Francisco to New York with the clock left on Pacific will
 * be greeted three hours late, and the fix for that is in the operating
 * system rather than here.
 */
export const FALLBACK_ZONE = 'America/New_York';

/** Where the browser thinks it is. */
export function localZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || FALLBACK_ZONE;
  } catch {
    return FALLBACK_ZONE;
  }
}

/** The hour of the day where the reader is, 0 to 23. */
export function hourInZone(now: Date = new Date(), zone: string = localZone()): number {
  const hour = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    hour: 'numeric',
    // `hour12: false` yields 24 at midnight in some engines; `h23` never does.
    hourCycle: 'h23',
  }).format(now);
  return Number(hour);
}

/**
 * What to say at this hour.
 *
 * Evening starts at six because that is when people stop expecting to be asked
 * for anything, not because of any astronomical fact.
 */
export function greetingAt(now: Date = new Date(), zone: string = localZone()): string {
  const hour = hourInZone(now, zone);
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

/** The Monday of this week where the reader is, as a person would write it. */
export function weekOf(now: Date = new Date(), zone: string = localZone()): string {
  // Read the date *in the zone* first. Near midnight the machine's own day can
  // be the one either side of the reader's, which would name the wrong week.
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now);
  const [year, month, day] = parts.split('-').map(Number);

  // Built as UTC so no local offset can shift the day back across a boundary.
  const today = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
  const monday = new Date(today);
  monday.setUTCDate(today.getUTCDate() - ((today.getUTCDay() + 6) % 7));

  return monday.toLocaleDateString('en-GB', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
